import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  ClipboardCheck,
  Clock3,
  FileCheck2,
  FilePenLine,
  FileText,
  Eye,
  Info,
  ListChecks,
  LoaderCircle,
  LockKeyhole,
  Mail,
  MessageSquareText,
  Plus,
  RefreshCw,
  Route,
  RotateCcw,
  Send,
  ShieldCheck,
  Sparkles,
  Target,
  Trash2,
  UploadCloud,
  UserRound,
  Wrench,
  X,
} from "lucide-react";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ConfirmDialog, useToast } from "@tribunalea/ui";
import { CivitaAssistantLauncher } from "../components/CivitaAssistant.jsx";
import { DocumentPreview } from "../components/DocumentPreview.jsx";

const outcomes = Object.freeze([
  { id: "refund", label: "Que me devuelvan el dinero", icon: CircleDollarSign },
  { id: "fulfill", label: "Que cumplan lo prometido", icon: ClipboardCheck },
  { id: "repair_replace", label: "Que reparen o reemplacen algo", icon: Wrench },
  { id: "cancel", label: "Que cancelen un cobro o contrato", icon: RotateCcw },
  { id: "correct_information", label: "Que corrijan información", icon: FilePenLine },
  { id: "response", label: "Que me den una respuesta", icon: MessageSquareText },
  { id: "other", label: "Otro resultado", icon: Target },
]);

const channels = Object.freeze([
  { id: "email", label: "Correo", icon: Mail },
  { id: "web_form", label: "Formulario web", icon: FileText },
  { id: "whatsapp", label: "WhatsApp", icon: MessageSquareText },
  { id: "letter", label: "Carta", icon: Send },
  { id: "in_person", label: "En persona", icon: UserRound },
]);

const demoClaim = Object.freeze({
  id: "reclamo-ejemplo",
  notebookId: "ejemplo",
  story: "Compré una heladera y llegó con la puerta golpeada. Avisé el mismo día, envié fotos y todavía no me dieron una solución.",
  recipientHint: "Tienda Hogar",
  sources: [
    { id: "comprobante", fileName: "factura-compra.pdf", status: "ready", pageCount: 1 },
    { id: "foto", fileName: "heladera-recibida.webp", status: "ready", pageCount: 1 },
  ],
  reviewStatus: "ready",
  generationStatus: "idle",
  status: "review",
  createdAt: new Date().toISOString(),
  review: {
    title: "Producto recibido con daños",
    recipient: { name: "Tienda Hogar", details: "Comercio donde se realizó la compra" },
    summary: "La compra fue entregada con un daño visible. El problema se informó el día de la entrega y todavía no hubo una solución concreta.",
    facts: [
      {
        date: "4 de septiembre de 2026",
        description: "Se compró una heladera por $860.000.",
        amount: "$860.000",
        evidence: [{ fileName: "factura-compra.pdf", page: 1, quote: "Total $860.000" }],
      },
      {
        date: "8 de septiembre de 2026",
        description: "La heladera fue entregada con un golpe visible en la puerta.",
        amount: null,
        evidence: [{ fileName: "heladera-recibida.webp", page: 1, quote: "Imagen adjunta" }],
      },
      {
        date: "8 de septiembre de 2026",
        description: "El problema se informó al comercio el mismo día de la entrega y se enviaron fotos.",
        amount: null,
        evidence: [],
      },
    ],
    identifiedDetails: [
      { label: "Importe", value: "$860.000", evidence: [] },
      { label: "Producto", value: "Heladera", evidence: [] },
    ],
    missingInformation: [
      { question: "¿Tenés un número de compra o de reclamo?", reason: "Ayuda al comercio a encontrar la operación rápidamente." },
    ],
    suggestedOutcomes: ["repair_replace", "response"],
    possibleActions: [
      {
        type: "direct_solution",
        title: "Pedir una solución directa",
        explanation: "Volver a contactar al comercio dejando por escrito el daño y la solución que esperás.",
        whatItCanAchieve: "Puede resolver el problema sin iniciar otra gestión.",
        whatYouNeed: ["Comprobante de compra", "Fotos del daño", "Datos de la compra"],
        nextStep: "Enviar un mensaje claro con las fotos y conservar una copia.",
        caution: "Pedí que cualquier respuesta o compromiso quede por escrito.",
        recommended: true,
      },
      {
        type: "formal_claim",
        title: "Preparar un reclamo formal",
        explanation: "Ordenar los hechos y hacer un pedido concreto al comercio por un canal que puedas conservar.",
        whatItCanAchieve: "Deja una constancia clara del problema y de la solución solicitada.",
        whatYouNeed: ["Fechas principales", "Importe", "Pruebas disponibles"],
        nextStep: "Revisar los datos de abajo y generar el borrador.",
        caution: null,
        recommended: false,
      },
      {
        type: "consumer_authority",
        title: "Consultar un organismo de consumo",
        explanation: "Si el comercio no responde, un organismo competente podría orientar o recibir el reclamo.",
        whatItCanAchieve: "Puede abrir una instancia adicional para buscar una solución.",
        whatYouNeed: ["Reclamo previo", "Comprobante", "Respuestas recibidas"],
        nextStep: "Confirmar cuál es el organismo y el procedimiento que corresponde en tu localidad.",
        caution: "Los requisitos y canales pueden variar según el lugar.",
        recommended: false,
      },
    ],
    cautions: ["Confirmá la fecha exacta de entrega antes de enviar el reclamo."],
  },
});

function formatDate(value) {
  if (!value) return "";
  return new Intl.DateTimeFormat("es-AR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
}

function formatSize(bytes) {
  if (!Number.isFinite(bytes)) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function ClaimProgress({ current }) {
  return (
    <ol className="claim-progress" aria-label="Progreso para resolver el problema">
      {["Tu problema", "Los hechos", "Tus opciones", "Tu reclamo"].map((label, index) => {
        const step = index + 1;
        const state = step < current ? "done" : step === current ? "current" : "pending";
        return (
          <li className={`is-${state}`} key={label}>
            <span>{state === "done" ? <Check size={14} /> : step}</span>
            <small>{label}</small>
          </li>
        );
      })}
    </ol>
  );
}

function ClaimProcessing({ claim, onBack }) {
  const filesReady = claim.sources?.every(({ status }) => status === "ready");
  const generating = claim.status === "generating";
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [claim.id, generating]);
  return (
    <main className="civita-document-page claim-page processing-view">
      <button className="text-button" type="button" onClick={onBack}>
        <ArrowLeft size={16} /> Volver
      </button>
      <ClaimProgress current={generating ? 4 : 2} />
      <section className="processing-card" aria-live="polite">
        <span className="processing-card__icon"><Sparkles size={25} /></span>
        <span className="eyebrow">Resolver un problema</span>
        <h1>{generating ? "Estamos preparando tu reclamo" : "Estamos entendiendo tu situación"}</h1>
        <p>{generating ? "Adaptamos el texto al objetivo, canal y tono que elegiste." : "Vamos a separar hechos, pruebas y datos que conviene confirmar antes de mostrarte caminos posibles."}</p>
        {generating ? (
          <ol>
            <li className="processing-step is-done"><span><Check size={15} /></span>Hechos confirmados</li>
            <li className="processing-step is-done"><span><Check size={15} /></span>Objetivo y canal elegidos</li>
            <li className="processing-step is-active"><span><LoaderCircle className="is-spinning" size={15} /></span>Redactando y verificando el borrador</li>
          </ol>
        ) : (
          <ol>
            <li className="processing-step is-done"><span><Check size={15} /></span>Relato recibido</li>
            {claim.sources?.length > 0 && (
              <li className={`processing-step is-${filesReady ? "done" : "active"}`}>
                <span>{filesReady ? <Check size={15} /> : <LoaderCircle className="is-spinning" size={15} />}</span>
                Leyendo los archivos adjuntos
              </li>
            )}
            <li className={`processing-step is-${filesReady ? "active" : "pending"}`}>
              <span>{filesReady ? <LoaderCircle className="is-spinning" size={15} /> : null}</span>
              Ordenando los hechos y preparando tus opciones
            </li>
          </ol>
        )}
        <small>{generating ? "Al terminar, podrás editar y descargar el texto." : "Primero vas a conocer tus opciones. El reclamo se prepara solamente si decidís continuar."}</small>
      </section>
    </main>
  );
}

function ClaimError({ claim, retrying, onRetry, onBack }) {
  return (
    <main className="civita-document-page claim-page processing-view">
      <section className="processing-card is-error">
        <span className="processing-card__icon"><AlertCircle size={25} /></span>
        <span className="eyebrow">No pudimos terminar</span>
        <h1>Necesitamos intentar otra vez</h1>
        <p>{claim.error?.message || "Ocurrió un problema al organizar la situación."}</p>
        <div className="button-row">
          <button className="primary-button" type="button" onClick={onRetry} disabled={retrying}>
            {retrying ? <LoaderCircle className="is-spinning" size={17} /> : <RefreshCw size={17} />}
            Reintentar
          </button>
          <button className="secondary-button" type="button" onClick={onBack}>Empezar otro</button>
        </div>
      </section>
    </main>
  );
}

function EvidenceBadges({ evidence }) {
  if (!evidence?.length) return <small className="claim-no-evidence">Contado por vos · sin archivo asociado</small>;
  return (
    <div className="claim-evidence-badges">
      {evidence.map((item, index) => (
        <span key={`${item.fileName}-${item.page}-${index}`} title={item.quote || ""}>
          <FileCheck2 size={13} /> {item.fileName}{item.page ? ` · pág. ${item.page}` : ""}
        </span>
      ))}
    </div>
  );
}

function ClaimReview({ claim, generating, onBack, onGenerate }) {
  const review = claim.review;
  const [stage, setStage] = useState(2);
  const [recipient, setRecipient] = useState(review.recipient?.name || claim.recipientHint || "");
  const [facts, setFacts] = useState(() => review.facts.map((fact) => ({ ...fact })));
  const [answers, setAnswers] = useState(() => review.missingInformation.map((item) => ({
    question: item.question,
    answer: "",
  })));
  const [selectedOutcomes, setSelectedOutcomes] = useState(() => (
    review.suggestedOutcomes?.length ? review.suggestedOutcomes : []
  ));
  const [customOutcome, setCustomOutcome] = useState("");
  const [claimantName, setClaimantName] = useState("");
  const [claimantContact, setClaimantContact] = useState("");
  const [channel, setChannel] = useState("email");
  const [tone, setTone] = useState("firm");
  const [additionalDetails, setAdditionalDetails] = useState("");
  const possibleActions = review.possibleActions?.length
    ? review.possibleActions
    : [
        {
          type: "direct_solution",
          title: "Intentar una solución directa",
          explanation: "Contactar a la otra parte con un pedido concreto y conservar la respuesta.",
          whatItCanAchieve: "Puede resolver el problema con una gestión simple.",
          whatYouNeed: ["Los datos principales del problema"],
          nextStep: "Definir qué solución querés pedir y comunicarla por un medio que puedas conservar.",
          caution: null,
          recommended: true,
        },
        {
          type: "formal_claim",
          title: "Preparar un reclamo",
          explanation: "Dejar los hechos y el pedido por escrito de manera clara.",
          whatItCanAchieve: "Ayuda a dejar constancia de lo sucedido y de la solución solicitada.",
          whatYouNeed: ["Hechos confirmados", "Un destinatario", "El resultado que esperás"],
          nextStep: "Completá la sección de abajo para generar un borrador.",
          caution: null,
          recommended: false,
        },
      ];

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [claim.id, stage]);

  function updateFact(index, changes) {
    setFacts((current) => current.map((fact, factIndex) => (
      factIndex === index ? { ...fact, ...changes } : fact
    )));
  }

  function toggleOutcome(id) {
    setSelectedOutcomes((current) => current.includes(id)
      ? current.filter((item) => item !== id)
      : [...current, id].slice(-4));
  }

  if (stage === 3) {
    return (
      <main className="civita-document-page claim-page claim-review-page">
        <button className="text-button" type="button" onClick={() => setStage(2)}>
          <ArrowLeft size={16} /> Revisar los hechos
        </button>
        <ClaimProgress current={3} />
        <header className="claim-section-heading">
          <span className="eyebrow">Paso 3 de 4</span>
          <h1>Estos son tus caminos posibles</h1>
          <p>Comparalos con calma. No hay una opción obligatoria y podés empezar por la más simple.</p>
        </header>

        <div className="claim-review-layout">
          <section className="claim-panel claim-objective-panel">
            <div className="claim-possible-actions">
              {possibleActions.map((action, index) => (
                <article className={action.recommended ? "is-recommended" : ""} key={`${action.type}-${index}`}>
                  <header>
                    <span><Route size={18} /></span>
                    <div>
                      {action.recommended && <small>Buen primer paso</small>}
                      <h2>{action.title}</h2>
                    </div>
                  </header>
                  <p>{action.explanation}</p>
                  <dl>
                    <div><dt>Para qué puede servir</dt><dd>{action.whatItCanAchieve}</dd></div>
                    <div><dt>Qué necesitás</dt><dd>{action.whatYouNeed.length ? action.whatYouNeed.join(" · ") : "No requiere documentación especial para empezar."}</dd></div>
                    <div><dt>Próximo paso</dt><dd>{action.nextStep}</dd></div>
                  </dl>
                  {action.caution && <p className="claim-action-caution"><AlertCircle size={14} /> {action.caution}</p>}
                </article>
              ))}
            </div>
            <p className="claim-options-note"><Info size={16} /> Estas son orientaciones prácticas basadas en lo que contaste, no una conclusión jurídica definitiva.</p>

            <div className="claim-form-divider" />
            <div className="claim-prepare-heading">
              <span className="eyebrow">Si querés reclamar</span>
              <h2>¿Qué querés conseguir?</h2>
              <p>Elegí el resultado que querés pedir. Podés seleccionar más de uno y preparar el texto ahora.</p>
            </div>
            <div className="outcome-grid">
              {outcomes.map(({ id, label, icon: Icon }) => {
                const selected = selectedOutcomes.includes(id);
                return (
                  <button
                    className={selected ? "is-selected" : ""}
                    type="button"
                    aria-pressed={selected}
                    key={id}
                    onClick={() => toggleOutcome(id)}
                  >
                    <span><Icon size={19} /></span>
                    <strong>{label}</strong>
                    <span className="outcome-check">{selected ? <Check size={14} /> : null}</span>
                  </button>
                );
              })}
            </div>
            {selectedOutcomes.includes("other") && (
              <label className="claim-field">
                <span>¿Qué otro resultado querés?</span>
                <textarea value={customOutcome} onChange={(event) => setCustomOutcome(event.target.value.slice(0, 1_000))} rows={3} />
              </label>
            )}

            <div className="claim-form-divider" />
            <h2>Cómo querés presentarlo</h2>
            <div className="channel-grid">
              {channels.map(({ id, label, icon: Icon }) => (
                <button
                  className={channel === id ? "is-selected" : ""}
                  type="button"
                  key={id}
                  onClick={() => setChannel(id)}
                >
                  <Icon size={17} /> {label}
                </button>
              ))}
            </div>
            <div className="claim-inline-fields">
              <label className="claim-field">
                <span>Tu nombre <small>Opcional</small></span>
                <input value={claimantName} onChange={(event) => setClaimantName(event.target.value.slice(0, 200))} placeholder="Nombre y apellido" />
              </label>
              <label className="claim-field">
                <span>Tu contacto <small>Opcional</small></span>
                <input value={claimantContact} onChange={(event) => setClaimantContact(event.target.value.slice(0, 400))} placeholder="Correo, teléfono o domicilio" />
              </label>
            </div>
            <label className="claim-field">
              <span>Destinatario</span>
              <input value={recipient} onChange={(event) => setRecipient(event.target.value.slice(0, 300))} placeholder="Empresa, persona u organismo" />
            </label>
            <label className="claim-field">
              <span>Algo más que deba aparecer <small>Opcional</small></span>
              <textarea value={additionalDetails} onChange={(event) => setAdditionalDetails(event.target.value.slice(0, 2_000))} placeholder="Por ejemplo, un número de compra o una gestión reciente." rows={3} />
            </label>
            <fieldset className="tone-choice">
              <legend>Tono</legend>
              <label><input type="radio" name="tone" checked={tone === "cordial"} onChange={() => setTone("cordial")} /> Cordial</label>
              <label><input type="radio" name="tone" checked={tone === "firm"} onChange={() => setTone("firm")} /> Firme y respetuoso</label>
            </fieldset>

            <button
              className="analyze-button claim-generate-button"
              type="button"
              disabled={!selectedOutcomes.length || generating}
              onClick={() => onGenerate({
                recipient,
                facts,
                answers,
                desiredOutcomes: selectedOutcomes,
                customOutcome,
                claimantName,
                claimantContact,
                channel,
                tone,
                additionalDetails,
              })}
            >
              {generating ? <LoaderCircle className="is-spinning" size={18} /> : <Sparkles size={18} />}
              {generating ? "Preparando tu reclamo…" : "Preparar mi reclamo"}
              {!generating && <ArrowRight size={18} />}
            </button>
          </section>

          <aside className="claim-side-note">
            <ShieldCheck size={20} />
            <h2>Vos elegís cómo avanzar</h2>
            <p>Podés usar solamente esta orientación o continuar con el reclamo. Civita no enviará nada automáticamente.</p>
          </aside>
        </div>
      </main>
    );
  }

  return (
    <main className="civita-document-page claim-page claim-review-page">
      <button className="text-button" type="button" onClick={onBack}>
        <ArrowLeft size={16} /> Mis situaciones
      </button>
      <ClaimProgress current={2} />
      <header className="claim-section-heading">
        <span className="eyebrow">Paso 2 de 4</span>
        <h1>Esto es lo que entendimos</h1>
        <p>Corregí cualquier dato que no sea exacto. Nada se enviará desde esta pantalla.</p>
      </header>

      <div className="claim-review-layout">
        <section className="claim-panel">
          <div className="claim-understood">
            <span><Sparkles size={18} /></span>
            <div><strong>{review.title}</strong><p>{review.summary}</p></div>
          </div>

          <label className="claim-field">
            <span>¿A quién va dirigido?</span>
            <input value={recipient} onChange={(event) => setRecipient(event.target.value.slice(0, 300))} placeholder="Empresa, persona u organismo" />
          </label>

          <div className="claim-facts-heading">
            <div><span className="eyebrow">Cronología</span><h2>Los hechos</h2></div>
            <button type="button" onClick={() => setFacts((current) => [...current, { date: null, description: "", amount: null, evidence: [] }])}>
              <Plus size={15} /> Agregar hecho
            </button>
          </div>
          <div className="claim-facts">
            {facts.map((fact, index) => (
              <article key={`fact-${index}`}>
                <span className="claim-fact-number">{index + 1}</span>
                <div>
                  <div className="claim-fact-meta">
                    <label><CalendarDays size={14} /><input value={fact.date || ""} onChange={(event) => updateFact(index, { date: event.target.value || null })} placeholder="Fecha, si la sabés" /></label>
                    <label><CircleDollarSign size={14} /><input value={fact.amount || ""} onChange={(event) => updateFact(index, { amount: event.target.value || null })} placeholder="Monto, si corresponde" /></label>
                  </div>
                  <textarea value={fact.description} onChange={(event) => updateFact(index, { description: event.target.value.slice(0, 900) })} rows={3} aria-label={`Descripción del hecho ${index + 1}`} />
                  <EvidenceBadges evidence={fact.evidence} />
                </div>
                {facts.length > 1 && (
                  <button className="claim-remove-fact" type="button" aria-label={`Eliminar hecho ${index + 1}`} onClick={() => setFacts((current) => current.filter((_, factIndex) => factIndex !== index))}>
                    <Trash2 size={15} />
                  </button>
                )}
              </article>
            ))}
          </div>

          {review.missingInformation.length > 0 && (
            <section className="claim-questions">
              <header><Info size={18} /><div><h2>Datos que pueden ayudar</h2><p>Si no los sabés, podés continuar igual.</p></div></header>
              {review.missingInformation.map((item, index) => (
                <label className="claim-field" key={item.question}>
                  <span>{item.question}</span>
                  <small>{item.reason}</small>
                  <input value={answers[index]?.answer || ""} onChange={(event) => setAnswers((current) => current.map((answer, answerIndex) => answerIndex === index ? { ...answer, answer: event.target.value.slice(0, 1_000) } : answer))} placeholder="Tu respuesta (opcional)" />
                </label>
              ))}
            </section>
          )}

          <button className="analyze-button" type="button" disabled={facts.some(({ description }) => !description.trim())} onClick={() => setStage(3)}>
            Ver mis opciones <ArrowRight size={18} />
          </button>
        </section>

        <aside className="claim-review-aside">
          {review.identifiedDetails.length > 0 && (
            <section>
              <span className="eyebrow">Datos encontrados</span>
              <dl>{review.identifiedDetails.map((item) => <div key={item.label}><dt>{item.label}</dt><dd>{item.value}</dd></div>)}</dl>
            </section>
          )}
          {review.cautions.length > 0 && (
            <section className="claim-cautions">
              <span className="eyebrow">Antes de seguir</span>
              <ul>{review.cautions.map((item) => <li key={item}><AlertCircle size={15} /> {item}</li>)}</ul>
            </section>
          )}
          <section className="claim-side-note">
            <FileCheck2 size={19} />
            <h2>Prueba y relato no son lo mismo</h2>
            <p>Marcamos qué hechos aparecen en tus archivos y cuáles conocemos solamente porque vos los contaste.</p>
          </section>
        </aside>
      </div>
    </main>
  );
}

function ClaimEvidenceDialog({ api, claim, initialPage, source, onClose }) {
  const [page, setPage] = useState(initialPage);
  const previewApi = useMemo(() => ({
    getDocumentBlob: () => api.getClaimSourceBlob(claim.id, source.id),
  }), [api, claim.id, source.id]);

  useEffect(() => {
    const close = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [onClose]);

  return (
    <div className="claim-evidence-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="claim-evidence-dialog" role="dialog" aria-modal="true" aria-label={`Evidencia: ${source.fileName}`} onMouseDown={(event) => event.stopPropagation()}>
        <button className="claim-evidence-close" type="button" onClick={onClose} aria-label="Cerrar evidencia"><X size={19} /></button>
        <DocumentPreview api={previewApi} document={source} page={page} onPageChange={setPage} label="Evidencia adjunta" />
      </section>
    </div>
  );
}

function ClaimResult({ api, claim, onBack, onDelete }) {
  const generated = claim.generated;
  const parameters = new URLSearchParams(window.location.search);
  const requestedSourceId = parameters.get("source");
  const requestedPage = Math.max(1, Number(parameters.get("page")) || 1);
  const [evidencePreview, setEvidencePreview] = useState(() => {
    const source = claim.sources.find(({ id }) => id === requestedSourceId);
    return source ? { source, page: requestedPage } : null;
  });
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [claim.id]);

  return (
    <main className="civita-document-page claim-page claim-result-page">
      {claim.demo && <div className="demo-banner"><Sparkles size={16} /> Este es un ejemplo ilustrativo. Ningún reclamo fue enviado.</div>}
      <header className="result-header">
        <button className="text-button" type="button" onClick={onBack}><ArrowLeft size={16} /> Mis situaciones</button>
        {!claim.demo && <button className="danger-icon-button" type="button" onClick={onDelete} title="Eliminar reclamo"><Trash2 size={17} /></button>}
      </header>
      <ClaimProgress current={4} />
      <section className="claim-result-heading">
        <div><span className="eyebrow">Tu reclamo está preparado</span><h1>{generated.title}</h1><p>{generated.subject}</p></div>
        <span className="analysis-ready"><CheckCircle2 size={16} /> Borrador listo</span>
      </section>

      <div className="claim-result-summary">
        <section>
          <span className="section-icon"><ListChecks size={18} /></span>
          <div><h2>Revisión antes de enviar</h2><p>{generated.deliveryAdvice}</p></div>
          <ul>
            {generated.checklist.map((item, index) => (
              <li className={`is-${item.status}`} key={`${item.label}-${index}`}>
                {item.status === "verified" ? <CheckCircle2 size={17} /> : <AlertCircle size={17} />}
                <span><strong>{item.label}</strong><small>{item.detail}</small></span>
              </li>
            ))}
          </ul>
        </section>
        {generated.warnings.length > 0 && (
          <aside><AlertCircle size={19} /><div><strong>Completá o verificá</strong><ul>{generated.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul></div></aside>
        )}
      </div>

      {claim.sources.length > 0 && (
        <section className="claim-result-evidence">
          <header><div><span className="eyebrow">Tus pruebas</span><h2>Archivos usados para preparar el reclamo</h2></div><small>{claim.sources.length} archivo{claim.sources.length === 1 ? "" : "s"}</small></header>
          <div>
            {claim.sources.map((source) => (
              <button type="button" key={source.id} onClick={() => setEvidencePreview({ source, page: 1 })}>
                <span><FileCheck2 size={17} /></span><span><strong>{source.fileName}</strong><small>{source.pageCount ? `${source.pageCount} pág.` : "Archivo adjunto"}</small></span><Eye size={17} />
              </button>
            ))}
          </div>
        </section>
      )}

      <CivitaAssistantLauncher
        className="claim-draft-section"
        eyebrow="Tu borrador"
        title="Editalo, copialo o descargalo"
        description="Abrí el editor flotante de Justiniano. Los cambios se guardan automáticamente en este navegador."
        sourceLabel="No se envía solo"
        label="Abrir borrador"
        initialView="drafting"
        initialDraft={{
          id: `civita-claim-${claim.id}`,
          title: generated.title,
          content: generated.draft,
        }}
        conversationScope={`reclamo:${claim.id}:v1`}
        conversationMetadata={{ civitaClaimId: claim.id }}
        notebookIds={claim.sources.length ? [claim.notebookId] : []}
        requestedSources={claim.sources.length ? ["library"] : []}
        welcome="Puedo ayudarte a ajustar este reclamo. Decime qué querés cambiar y no agregaré hechos, leyes ni plazos que no estén confirmados."
        suggestions={["Hacelo más breve", "Usá un tono más firme", "¿Qué debería verificar antes de enviarlo?"]}
        appContext={{
          route: "/resolver-problema",
          activeEntity: { type: "civita_claim", id: claim.id, label: generated.title },
          claim: {
            story: claim.story,
            recipientHint: claim.recipientHint,
            review: claim.review,
            confirmedInput: claim.confirmedInput,
            generated,
          },
        }}
      />
      <p className="ai-disclaimer">Este borrador fue generado con IA y puede equivocarse. Verificá nombres, fechas, montos y datos de contacto antes de enviarlo.</p>
      {evidencePreview && (
        <ClaimEvidenceDialog
          api={api}
          claim={claim}
          initialPage={evidencePreview.page}
          source={evidencePreview.source}
          onClose={() => {
            setEvidencePreview(null);
            window.history.replaceState({}, "", `/resolver-problema?claim=${encodeURIComponent(claim.id)}`);
          }}
        />
      )}
    </main>
  );
}

function demoGenerated(input) {
  const requested = input.desiredOutcomes.includes("repair_replace")
    ? "solicito el reemplazo del producto por uno en buen estado o, en su defecto, una solución concreta equivalente"
    : "solicito una respuesta concreta y una solución al problema informado";
  return {
    ...demoClaim,
    demo: true,
    confirmedInput: input,
    generationStatus: "ready",
    status: "ready",
    generated: {
      title: "Reclamo por producto recibido con daños",
      subject: "Pedido de solución por heladera entregada con un golpe",
      draft: `Asunto: Reclamo por producto recibido con daños\n\nA Tienda Hogar:\n\nMe comunico por la compra de una heladera realizada el 4 de septiembre de 2026 por un total de $860.000.\n\nEl producto fue entregado el 8 de septiembre de 2026 con un golpe visible en la puerta. Informé el problema ese mismo día y envié fotografías, pero hasta el momento no recibí una solución concreta.\n\nPor este motivo, ${requested}. También solicito que me informen cómo y cuándo se realizará la gestión correspondiente.\n\nAdjunto la factura de compra y las fotografías tomadas al recibir el producto.\n\nAgradezco una pronta respuesta.\n\n${input.claimantName || "[Tu nombre]"}\n${input.claimantContact || "[Tu contacto]"}`,
      checklist: [
        { label: "Producto, monto y fecha de compra", status: "verified", detail: "Coinciden con la información revisada." },
        { label: "Número de compra o reclamo", status: "review", detail: "Conviene agregarlo si está disponible." },
      ],
      warnings: input.claimantName ? [] : ["Completá tu nombre y un dato de contacto antes de enviar."],
      deliveryAdvice: "Adjuntá la factura y las fotos. Conservá una copia del mensaje enviado y de cualquier respuesta que recibas.",
    },
  };
}

export function PrepareClaimPage({ api, authEnabled }) {
  const { showToast } = useToast();
  const fileInputRef = useRef(null);
  const [story, setStory] = useState("");
  const [recipientHint, setRecipientHint] = useState("");
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [claims, setClaims] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(authEnabled);
  const [submitting, setSubmitting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  function selectClaim(claim) {
    setSelected(claim);
    if (!claim?.demo) {
      window.history.replaceState({}, "", `/resolver-problema?claim=${encodeURIComponent(claim.id)}`);
    }
  }

  function resetView() {
    setSelected(null);
    setStory("");
    setRecipientHint("");
    setFiles([]);
    window.history.replaceState({}, "", "/resolver-problema");
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }

  useEffect(() => {
    if (!authEnabled) return undefined;
    let active = true;
    api.listClaims()
      .then((items) => {
        if (!active) return;
        setClaims(items);
        const parameters = new URLSearchParams(window.location.search);
        const requestedId = parameters.get("claim");
        const requestedNotebook = parameters.get("notebook");
        const requestedSource = parameters.get("source");
        const match = items.find((item) => (
          (requestedId && item.id === requestedId)
          || (requestedNotebook && item.notebookId === requestedNotebook)
          || (requestedSource && item.sources.some(({ id }) => id === requestedSource))
        ));
        if (match) setSelected(match);
      })
      .catch((error) => showToast({ message: error.message, tone: "danger" }))
      .finally(() => active && setLoadingHistory(false));
    return () => { active = false; };
  }, [api, authEnabled, showToast]);

  useEffect(() => {
    if (!selected || selected.demo || !["processing", "reviewing", "generating"].includes(selected.status)) return undefined;
    let active = true;
    let timer;
    async function refresh() {
      try {
        let current = await api.getClaim(selected.id);
        const sourcesReady = current.sources.every(({ status }) => status === "ready");
        if (sourcesReady && ["waiting", "analyzing"].includes(current.reviewStatus)) current = await api.analyzeClaim(current.id);
        if (!active) return;
        setSelected(current);
        setClaims((items) => items.map((item) => item.id === current.id ? current : item));
        if (["processing", "reviewing", "generating"].includes(current.status)) timer = window.setTimeout(refresh, 1_800);
      } catch (error) {
        if (!active) return;
        if (error.code === "civita_claim_not_ready") {
          timer = window.setTimeout(refresh, 1_800);
          return;
        }
        setSelected((current) => current ? { ...current, status: "failed", error: { code: error.code, message: error.message } } : current);
      }
    }
    void refresh();
    return () => { active = false; window.clearTimeout(timer); };
  }, [api, selected?.demo, selected?.id, selected?.status]);

  function chooseFiles(fileList) {
    const next = [...(fileList || [])];
    if (!next.length) return;
    if (next.length > 10) {
      showToast({ message: "Podés adjuntar hasta 10 archivos.", tone: "danger" });
      return;
    }
    const invalid = next.some((file) => !["application/pdf", "image/jpeg", "image/png", "image/webp"].includes(file.type) && !/\.(?:pdf|jpe?g|png|webp)$/i.test(file.name));
    if (invalid) {
      showToast({ message: "Usá archivos PDF o imágenes JPG, PNG o WEBP.", tone: "danger" });
      return;
    }
    if (next.reduce((total, file) => total + file.size, 0) > 20 * 1024 * 1024) {
      showToast({ message: "Los archivos superan el límite total de 20 MB.", tone: "danger" });
      return;
    }
    setFiles(next);
  }

  async function submit(event) {
    event.preventDefault();
    if (story.trim().length < 20 || submitting) return;
    if (!authEnabled) {
      selectClaim({ ...demoClaim, demo: true });
      return;
    }
    setSubmitting(true);
    try {
      const created = await api.createClaim({ story, recipientHint, files });
      setClaims((items) => [created, ...items]);
      selectClaim(created);
    } catch (error) {
      showToast({ message: error.message, tone: "danger" });
    } finally {
      setSubmitting(false);
    }
  }

  async function generate(input) {
    if (generating) return;
    if (selected.demo) {
      setSelected(demoGenerated(input));
      return;
    }
    setGenerating(true);
    try {
      const current = await api.generateClaim(selected.id, input);
      setSelected(current);
      setClaims((items) => items.map((item) => item.id === current.id ? current : item));
    } catch (error) {
      showToast({ message: error.message, tone: "danger" });
      const current = await api.getClaim(selected.id).catch(() => null);
      if (current) setSelected(current.status === "review" ? current : { ...current, status: "review" });
    } finally {
      setGenerating(false);
    }
  }

  async function retry() {
    if (!selected || selected.demo || retrying) return;
    setRetrying(true);
    try {
      const current = await api.retryClaim(selected.id);
      setSelected(current);
      setClaims((items) => items.map((item) => item.id === current.id ? current : item));
    } catch (error) {
      showToast({ message: error.message, tone: "danger" });
    } finally {
      setRetrying(false);
    }
  }

  async function remove() {
    if (!selected || deleting) return;
    setDeleting(true);
    try {
      await api.deleteClaim(selected.id);
      setClaims((items) => items.filter(({ id }) => id !== selected.id));
      setDeleteOpen(false);
      resetView();
      showToast({ message: "La situación fue eliminada.", tone: "success" });
    } catch (error) {
      showToast({ message: error.message, tone: "danger" });
    } finally {
      setDeleting(false);
    }
  }

  if (selected?.status === "ready" && selected.generated) {
    return (
      <>
        <ClaimResult api={api} claim={selected} onBack={resetView} onDelete={() => setDeleteOpen(true)} />
        {deleteOpen && <ConfirmDialog title="¿Eliminar esta situación?" description="Se borrarán el relato, los archivos adjuntos, las opciones y cualquier borrador preparado. Esta acción no se puede deshacer." confirmLabel="Eliminar" busy={deleting} onClose={() => !deleting && setDeleteOpen(false)} onConfirm={remove} />}
      </>
    );
  }
  if (selected?.status === "review" && selected.review) return <ClaimReview claim={selected} generating={generating} onBack={resetView} onGenerate={generate} />;
  if (selected?.status === "failed") return <ClaimError claim={selected} retrying={retrying} onRetry={retry} onBack={resetView} />;
  if (selected) return <ClaimProcessing claim={selected} onBack={resetView} />;

  return (
    <main className="civita-document-page claim-page claim-start-page">
      <ClaimProgress current={1} />
      <header className="upload-heading claim-start-heading">
        <span className="eyebrow">Resolver un problema</span>
        <h1>Contanos qué pasó.<br />Veamos qué podés hacer.</h1>
        <p>Ordenamos los hechos, te mostramos caminos posibles y, si decidís reclamar, preparamos un texto claro para que lo revises.</p>
      </header>

      <div className="upload-layout claim-start-layout">
        <form className="upload-form claim-start-form" onSubmit={submit}>
          <label className="claim-field claim-story-field">
            <span>¿Qué pasó?</span>
            <textarea value={story} onChange={(event) => setStory(event.target.value.slice(0, 8_000))} placeholder="Contalo con tus palabras. Por ejemplo: qué compraste o contrataste, qué salió mal, cuándo pasó y si ya intentaste resolverlo." rows={7} />
            <small>{story.trim().length < 20 ? "Incluí al menos un poco de contexto para poder ayudarte." : `${story.length.toLocaleString("es-AR")} de 8.000 caracteres`}</small>
          </label>
          <label className="claim-field">
            <span>¿Con quién es el problema? <small>Opcional</small></span>
            <input value={recipientHint} onChange={(event) => setRecipientHint(event.target.value.slice(0, 300))} placeholder="Empresa, comercio, persona u organismo" />
          </label>

          <div className="claim-attachments-heading"><div><strong>Agregá tus pruebas</strong><small>Opcional · facturas, contratos, correos, capturas o fotos.</small></div>{files.length > 0 && <span>{files.length} de 10</span>}</div>
          <div
            className={`claim-drop-zone ${dragging ? "is-dragging" : ""}`}
            onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => { event.preventDefault(); setDragging(false); chooseFiles(event.dataTransfer.files); }}
          >
            <input ref={fileInputRef} type="file" accept="application/pdf,.pdf,image/jpeg,.jpg,.jpeg,image/png,.png,image/webp,.webp" multiple onChange={(event) => chooseFiles(event.target.files)} />
            <button type="button" onClick={() => fileInputRef.current?.click()}><UploadCloud size={21} /><span><strong>Elegir archivos</strong><small>PDF, JPG, PNG o WEBP · hasta 20 MB en total</small></span></button>
          </div>
          {files.length > 0 && (
            <div className="claim-selected-files">
              {files.map((file, index) => (
                <div key={`${file.name}-${file.size}-${index}`}><span><FileCheck2 size={17} /></span><span><strong>{file.name}</strong><small>{formatSize(file.size)}</small></span><button type="button" aria-label={`Quitar ${file.name}`} onClick={() => setFiles((current) => current.filter((_, fileIndex) => fileIndex !== index))}><X size={15} /></button></div>
              ))}
            </div>
          )}

          <button className="analyze-button" type="submit" disabled={story.trim().length < 20 || submitting}>
            {submitting ? <LoaderCircle className="is-spinning" size={18} /> : <Sparkles size={18} />}
            {submitting ? "Guardando tu información…" : "Ver qué puedo hacer"}
            {!submitting && <ArrowRight size={18} />}
          </button>
          {!authEnabled && <button className="example-button" type="button" onClick={() => selectClaim({ ...demoClaim, demo: true })}>Ver un ejemplo completo</button>}
          <p className="privacy-note"><LockKeyhole size={14} />Tu relato y tus archivos quedan protegidos en tu espacio personal.</p>
        </form>

        <aside className="how-it-works claim-how-it-works">
          <span className="eyebrow">Cómo te ayudamos</span>
          <ol>
            <li><span>1</span><div><strong>Separamos los hechos</strong><small>Fechas, montos, personas y gestiones realizadas.</small></div></li>
            <li><span>2</span><div><strong>Marcamos qué los respalda</strong><small>Vinculamos cada dato con tus archivos cuando sea posible.</small></div></li>
            <li><span>3</span><div><strong>Te mostramos opciones</strong><small>Qué podría lograr cada camino y cómo empezar.</small></div></li>
            <li><span>4</span><div><strong>Preparamos el reclamo</strong><small>Solo si decidís avanzar, adaptado al canal y tono que elijas.</small></div></li>
          </ol>
          <div className="scope-note"><ShieldCheck size={18} /><p><strong>Nunca se envía automáticamente.</strong> Vos conservás el control del texto y de las pruebas.</p></div>
        </aside>
      </div>

      {(loadingHistory || claims.length > 0) && (
        <section className="recent-documents recent-claims">
          <header><div><span className="eyebrow">Tus situaciones</span><h2>Trabajos recientes</h2></div></header>
          {loadingHistory ? <div className="history-loading"><LoaderCircle className="is-spinning" size={18} /> Cargando…</div> : (
            <div className="document-list">
              {claims.map((claim) => (
                <button type="button" key={claim.id} onClick={() => selectClaim(claim)}>
                  <span className="document-list__icon"><FilePenLine size={19} /></span>
                  <span><strong>{claim.generated?.title || claim.review?.title || claim.recipientHint || "Situación en revisión"}</strong><small>{formatDate(claim.createdAt)} · {claim.sources.length ? `${claim.sources.length} archivo${claim.sources.length === 1 ? "" : "s"}` : "sin adjuntos"}</small></span>
                  {claim.status === "ready" || claim.status === "review" ? <CheckCircle2 className="is-ready" size={18} /> : claim.status === "failed" ? <AlertCircle className="is-failed" size={18} /> : <Clock3 className="is-pending" size={18} />}
                  <ChevronRight size={17} />
                </button>
              ))}
            </div>
          )}
        </section>
      )}
    </main>
  );
}
